<template>
  <div class="welcome-page">
    <!-- Mobile Hamburger Menu Button -->
    <div class="mobile-nav-toggle d-lg-none" @click="toggleSidebar">
      <i class="bi bi-list"></i>
    </div>

    <!-- ✅ Sidebar Navigation (Hidden by default) -->
    <aside :class="['sidebar', { 'sidebar-open': isSidebarOpen }]">
      <div class="sidebar-header d-flex justify-content-between align-items-center">
        <a class="logo" href="/">
          <i class="bi bi-mortarboard-fill me-2"></i> MyTuitionWave
        </a>
        <button class="btn-close" @click="toggleSidebar"></button>
      </div>
      <nav class="sidebar-nav mt-4">
        <ul class="list-unstyled">
          <li>
            <a class="nav-link" href="#about" @click="toggleSidebar">
              <i class="bi bi-info-circle me-3"></i> About
            </a>
          </li>
          <li>
            <a class="nav-link" href="#features" @click="toggleSidebar">
              <i class="bi bi-star-fill me-3"></i> Features
            </a>
          </li>
          <li>
            <a class="nav-link" href="#packages&pricing" @click="toggleSidebar">
              <i class="bi bi-box-seam-fill me-3"></i> Packages
            </a>
          </li>
          <li>
            <router-link class="nav-link login-btn" to="/login" @click="toggleSidebar">
              <i class="bi bi-box-arrow-in-right me-3"></i> Login
            </router-link>
          </li>
        </ul>
      </nav>
    </aside>

    <!-- Overlay to dim content when sidebar is open -->
    <div :class="['sidebar-overlay', { 'show-overlay': isSidebarOpen }]" @click="toggleSidebar"></div>

    <!-- ✅ Header Section -->
    <header class="welcome-header">
      <div class="logo-container mb-4">
        <img src="@/assets/TuitionWave.png" alt="MyTuitionWave Logo" class="logo-3d" />
      </div>
      <h6>MyTuitionWave.com</h6>
      <h1 class="shadow-text">Welcome to <span class="highlight">Our Tuition Institute</span></h1>
      <p class="lead">Your gateway to academic excellence.</p>
      <div class="button-group">
        <a href="#home" class="btn btn-lg btn-3d-primary hover-grow">Learn More</a>
        <router-link to="/login" class="btn btn-lg btn-outline-secondary hover-grow">Login</router-link>
      </div>
    </header>

    <!-- ✅ Features Section -->
    <section class="features" id="features">
      <div class="container">
        <div class="row g-4">
          <div class="col-md-4">
            <div class="feature-box shadow-3d hover-3d">
              <i class="bi bi-book-fill text-gradient-primary icon-3d"></i>
              <h3>Expert Tutors</h3>
              <p>Learn from the best in the field with years of experience.</p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="feature-box shadow-3d hover-3d">
              <i class="bi bi-calendar-check-fill text-gradient-primary icon-3d"></i>
              <h3>Flexible Schedules</h3>
              <p>Choose the time that suits you best with our flexible scheduling.</p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="feature-box shadow-3d hover-3d">
              <i class="bi bi-people-fill text-gradient-primary icon-3d"></i>
              <h3>Interactive Classes</h3>
              <p>Engage in interactive classes that make learning fun and effective.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ✅ Packages Section -->
    <PackageSection id="packages" />

    <!-- ✅ Footer -->
    <footer class="welcome-footer">
      <p>&copy; {{ currentYear }} MyTuitionWave.com | Powered by MySoftCode.com</p>
    </footer>
  </div>
</template>

<script>
import PackageSection from "@/components/HomeDashboard/PackageSection.vue";

export default {
  name: "WelcomePage",
  components: {
    PackageSection,
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      isSidebarOpen: false,
    };
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
  },
};
</script>

<style scoped>
/*
  🎨 Professional Color Palette
  - Primary: #0d6efd (Bootstrap Blue)
  - Secondary: #6c757d (Muted Gray)
  - Dark: #212529 (Deep Black/Gray)
  - Success: #198754 (Vibrant Green)
  - Info: #0dcaf0 (Cyan)
  - Warning: #ffc107 (Yellow)
  - Danger: #dc3545 (Red)
*/

/* --- General Enhancements --- */
body {
  font-family: 'Poppins', sans-serif; /* A more modern font */
  overflow-x: hidden; /* Prevents horizontal scroll from sidebar */
}

/* --- Sidebar Specific Styles --- */
.mobile-nav-toggle {
  position: fixed;
  top: 15px;
  right: 15px; /* Changed from right to left */
  font-size: 2rem;
  color: #0d6efd;
  cursor: pointer;
  z-index: 1051;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0 0.5rem;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}
.mobile-nav-toggle:hover {
  transform: scale(1.1);
}

.sidebar {
  position: fixed;
  top: 0;
  left: -300px; /* Changed from right to left */
  width: 280px;
  height: 100vh;
  background: #fff;
  z-index: 1050;
  padding: 20px;
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1); /* Changed box-shadow direction */
  transition: left 0.4s ease-in-out;
  display: flex;
  flex-direction: column;
}

.sidebar-open {
  left: 0; /* Changed from right to left */
}

.sidebar-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.sidebar .logo {
  font-size: 1.5rem;
  font-weight: bold;
  color: #0d6efd;
  text-decoration: none;
}

.sidebar .btn-close {
  font-size: 1.25rem;
}

.sidebar-nav .nav-link {
  display: flex;                /* Make link a flex container */
  align-items: center;          /* Vertically align icon + text */
  gap: 10px;                    /* Space between icon and text */
  color: #333;
  padding: 12px 15px;
  border-radius: 8px;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.sidebar-nav .nav-link i {
  font-size: 1.2rem;            /* Keep icons uniform */
  min-width: 20px;              /* Ensures equal spacing */
  color: #6c757d;
  transition: color 0.3s ease;
}

.sidebar-nav .nav-link:hover {
  background-color: #0d6efd;
  color: #fff;
}

.sidebar-nav .nav-link:hover i {
  color: #fff;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.4s ease;
}
.sidebar-overlay.show-overlay {
  opacity: 1;
  visibility: visible;
}

/* Hide Bootstrap navbar on desktop */
.navbar {
  display: none !important;
}


.logo-3d {
  width: 150px;
  height: auto;
  border-radius: 5%;
  /* Removed box-shadow */
  /* box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2); */
  filter: drop-shadow(0px 10px 20px rgba(0, 0, 0, 0.2)); /* Applied drop-shadow */
  transition: transform 0.4s ease-in-out, filter 0.4s ease-in-out; /* Updated transition for filter */
}

h6{
  color:rgb(17, 0, 255);
}
@media (min-width: 992px) {
  .navbar {
    display: flex !important; /* Show on larger screens if needed */
  }
  .mobile-nav-toggle, .sidebar, .sidebar-overlay {
    display: none;
  }
}

/* --- Existing Styles from previous request (kept for consistency) --- */
.shadow-text {
  text-shadow: 3px 3px 10px rgba(0, 0, 0, 0.2);
}
.feature-box {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.shadow-3d {
  border-radius: 1rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1), 0 6px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease-in-out;
  border: none;
}
.hover-3d:hover {
  transform: translateY(-12px) scale(1.03);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
}

.icon-3d {
  filter: drop-shadow(2px 3px 4px rgba(0, 0, 0, 0.3));
  font-size: 3rem;
  margin-bottom: 1rem;
}

.btn-3d-primary {
  background: linear-gradient(135deg, #0d6efd, #0b5ed7);
  border: none;
  color: #fff;
  border-radius: 50px;
  padding: 12px 30px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.btn-3d-primary::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150%;
  height: 150%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%) rotate(45deg);
  transition: all 0.5s ease;
  opacity: 0;
}
.btn-3d-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  color: #fff;
}
.btn-3d-primary:hover::after {
  opacity: 1;
}

.hover-grow:hover {
  transform: scale(1.05);
}

.text-gradient-primary {
  background: linear-gradient(45deg, #0d6efd, #0dcaf0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-outline-secondary {
  border-radius: 50px;
  padding: 12px 30px;
  transition: all 0.3s ease;
}

/* Additional styles for the main content */
.welcome-header {
  padding: 80px 0;
}
.welcome-header h1 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}
.welcome-header .highlight {
  color: #007bff;
}
.welcome-header .lead {
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
}
.welcome-header .button-group {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.features {
  padding: 50px 0;
}
.features h3 {
  font-size: 1.5rem;
  margin-bottom: 10px;
}
.features p {
  font-size: 1rem;
}

.welcome-footer {
  background: #f1f1f1;
  padding: 20px;
  position: relative; /* Change to relative to avoid fixed positioning issues */
  width: 100%;
  bottom: 0;
  margin-top: 50px;
}
.welcome-footer p {
  margin: 0;
  font-size: 0.875rem;
}
</style>
